import { loadFacetsFromSession } from '@/components/TeamAndSchedule/utils/filterSessionStorage';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';

type FacetTableMap<T = unknown> = Record<string, Record<string, { value?: T; matchMode?: string }>>;

/**
 * Try to load a single facet value from sessionStorage.
 * Returns the facet data object or `undefined` if not found.
 */
export function loadFacetFromSession<T = unknown>(
    activeTable: string,
    facetId: string
): { value?: T; matchMode?: string } | undefined {
    const sessionFacets = loadFacetsFromSession();
    if (!sessionFacets) return undefined;

    const tableFacets = (sessionFacets as unknown as FacetTableMap<T>)[activeTable];
    return tableFacets?.[facetId];
}

/**
 * Try to load a single facet value from URL query-string `facets` param.
 * Returns the facet data object or `undefined` on parse failure / not found.
 */
export function loadFacetFromUrl<T = unknown>(
    activeTable: string,
    facetId: string
): { value?: T; matchMode?: string } | undefined {
    const facetsParam = new URLSearchParams(window.location.search).get('facets');
    if (!facetsParam) return undefined;

    try {
        const facets = JSON.parse(facetsParam);
        return facets[activeTable]?.[facetId] ?? undefined;
    } catch (e) {
        const logger = useLogger('TeamSchedule -> FacetSessionLoader');
        logger.error('Failed to parse facets from URL', e);
        return undefined;
    }
}

/**
 * Load facet data from sessionStorage first, falling back to URL params.
 */
export function loadFacetData<T = unknown>(
    activeTable: string,
    facetId: string
): { value?: T; matchMode?: string } | undefined {
    return loadFacetFromSession<T>(activeTable, facetId)
        ?? loadFacetFromUrl<T>(activeTable, facetId);
}
